import type { useNavigate } from '@tanstack/react-router';
import type { NotificationHref } from './format-notification';

/**
 * Navigate to the deep-link a notification points at. Extracted from
 * `NotificationsBell` so the desktop OS-toast click handler can reuse
 * the exact same routing logic — keeps "what does it mean to click a
 * notification" in one place.
 *
 * Calendar events have no dedicated detail route in v1; this helper
 * mirrors the bell's fallback behaviour (project calendar when a
 * project is set, workspace home otherwise). `none` is a no-op so
 * legacy or unparseable rows don't throw.
 */
type Navigate = ReturnType<typeof useNavigate>;

export const navigateToNotificationHref = async (
  navigate: Navigate,
  href: NotificationHref,
): Promise<void> => {
  if (href.kind === 'task') {
    await navigate({
      to: '/w/$workspaceSlug/t/$taskUlid',
      params: {
        workspaceSlug: href.workspaceSlug,
        taskUlid: href.taskUlid,
      },
    });
    return;
  }
  if (href.kind === 'note') {
    await navigate({
      to: '/w/$workspaceSlug/n/$noteUlid',
      params: {
        workspaceSlug: href.workspaceSlug,
        noteUlid: href.noteUlid,
      },
    });
    return;
  }
  if (href.kind === 'calendar-event') {
    if (href.projectSlug) {
      await navigate({
        to: '/w/$workspaceSlug/p/$projectSlug/calendar',
        params: {
          workspaceSlug: href.workspaceSlug,
          projectSlug: href.projectSlug,
        },
      });
      return;
    }
    await navigate({
      to: '/w/$workspaceSlug',
      params: { workspaceSlug: href.workspaceSlug },
    });
  }
  // href.kind === 'none' → intentionally no-op
};
